import moment from "moment";

import { formatCurrencyRange, formatNumericCurrency } from "utils";

import { CurrencyCode } from "consts";

import { PRACTICE_SLUG, SingleFieldOutcomes, ValidAggregation } from "models";

export type PayoutStatus = "onTrack" | "keepRolling" | "needsAttention" | "noPayouts" | "awaitingData" | "inVerification" | "verified";

export type PayoutBarData = {
  actual: number;
  eligible: number;
  notEligible: number | null;
  isNegative: boolean;
  isFutureYear: boolean;
  minPotential?: number;
  maxPotential?: number;
};

export type Co2BarData = {
  actual: number;
  maxPayoutImpact: number | null;
  notEligible: number | null;
  baseline: number;
  isNegative: boolean;
  isFutureYear: boolean;
};

export type PayoutProgressState = "onTrack" | "keepRolling" | "needsAttention";

const KG_TO_TONNES_DIVISOR = 1000;
export const SOIL_PLUS_3_START_YEAR = 2026;

/**
 * Generates an array of single monitoring years from program start to one year after the current year.
 * Excludes multi-year ranges.
 */
export const generateMonitoringYears = (programStartYear: number | undefined, reportingYear: number): number[] => {
  const startYear = programStartYear ?? reportingYear;
  const endYear = Math.max(reportingYear + 1, moment().year() + 1);
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y += 1) {
    years.push(y);
  }
  return years;
};

/**
 * Calculates total enrolled hectares from field sizes for fields that are participating.
 */
export const calculateTotalEnrolledHa = (singleFieldOutcomes: SingleFieldOutcomes[], fieldSizes: Record<string, number>): number =>
  singleFieldOutcomes.reduce((total, field) => {
    const cropsOutcome = field.fieldOutcomes[PRACTICE_SLUG.CROPS];
    if (!cropsOutcome?.isParticipating) return total;
    return total + (fieldSizes[field.fieldId] || 0);
  }, 0);

// calculate allotments for bonus and err in kg from tonnes
export const calculateAllotmentPayouts = (
  errAllotment: number,
  bonusAllotmentAmount: number,
  pointPrice: number,
  previousErrAllotment?: number
): { bonusPayout: number; eligiblePayout: number; previousEligiblePayout?: number } => ({
  eligiblePayout: errAllotment * pointPrice * KG_TO_TONNES_DIVISOR,
  bonusPayout: bonusAllotmentAmount * pointPrice * KG_TO_TONNES_DIVISOR,
  previousEligiblePayout: previousErrAllotment ? previousErrAllotment * pointPrice * KG_TO_TONNES_DIVISOR : undefined,
});

/**
 * The field management payout capped at the farm's Soil+ 3 allotment for the year, as the home page and the fields page show it.
 * Null while the farm has no allotment yet; an allotment of 0 is a known figure of 0.
 */
export const allotmentCappedPayout = (
  soilPlus3Data: { errAllotment?: number | null; bonusAllotmentAmount?: number | null; previousErrAllotment?: number } | undefined,
  pointPrice: number,
  totalPayoutMin: number
): number | null => {
  if (soilPlus3Data?.errAllotment == null) return null;
  const { eligiblePayout } = calculateAllotmentPayouts(
    soilPlus3Data.errAllotment,
    soilPlus3Data.bonusAllotmentAmount ?? 0,
    pointPrice,
    soilPlus3Data.previousErrAllotment
  );
  return capPayoutAtEligible(totalPayoutMin, eligiblePayout);
};

/**
 * Caps an actual payout at the allotment-derived eligible payout ceiling.
 * An eligiblePayout of 0 means no allotment yet, so the capped result is 0 regardless of the actual payout.
 */
export const capPayoutAtEligible = (totalPayout: number, eligiblePayout: number): number =>
  eligiblePayout === 0 || (eligiblePayout > 0 && totalPayout > eligiblePayout) ? eligiblePayout : totalPayout;

/**
 * Determines the payout status badge to display based on payout data and state.
 */
export const determinePayoutStatus = ({
  payoutMin,
  eligible,
  isFutureYear,
  hasDataEntry,
  isPastPoP,
  paymentStatus,
}: {
  payoutMin: number;
  eligible: number;
  isFutureYear: boolean;
  hasDataEntry: boolean;
  isPastPoP: boolean;
  paymentStatus?: "paid" | "pending" | "inVerification";
}): PayoutStatus | null => {
  if (paymentStatus === "paid") return "verified";
  if (paymentStatus === "inVerification") return "inVerification";
  if (isFutureYear || (!hasDataEntry && !isPastPoP)) return "awaitingData";
  if (payoutMin < 0) return "noPayouts";
  if (eligible <= 0) return null;

  const ratio = Math.max(0, payoutMin) / eligible;

  if (ratio > 0.5) return "onTrack";
  if (ratio >= 0.25) return "keepRolling";
  if (ratio > 0) return "needsAttention";

  return null;
};

const isValidNonBaselineYearWithPayout = (yearData: ValidAggregation | undefined): boolean =>
  !!yearData?.validation?.valid && !yearData.isBaselineYear && typeof yearData.payouts?.avg === "number";

const fieldHasAllParticipatingPracticesValidForYear = (field: SingleFieldOutcomes, year: number): boolean => {
  const participatingPractices = Object.values(PRACTICE_SLUG)
    .map((practiceSlug) => field.fieldOutcomes[practiceSlug])
    .filter((practiceOutcome) => practiceOutcome?.isParticipating);

  if (participatingPractices.length === 0) return false;

  return participatingPractices.every((practiceOutcome) => isValidNonBaselineYearWithPayout(practiceOutcome?.years?.[year] as ValidAggregation | undefined));
};

/**
 * Checks if any field has data entry for a given year.
 */
export const hasDataEntryForYear = (singleFieldOutcomes: SingleFieldOutcomes[], year: number, isSoilPlus3Active: boolean): boolean =>
  singleFieldOutcomes.some((field) => {
    if (isSoilPlus3Active && year >= SOIL_PLUS_3_START_YEAR) return fieldHasAllParticipatingPracticesValidForYear(field, year);

    const cropsOutcome = field.fieldOutcomes[PRACTICE_SLUG.CROPS];
    if (!cropsOutcome?.isParticipating) return false;

    return isValidNonBaselineYearWithPayout(cropsOutcome?.years?.[year] as ValidAggregation | undefined);
  });

/**
 * Checks if the selected year is a past period of performance (already reported, before current reporting year).
 */
export const isPastPeriodOfPerformance = (selectedYear: number, reportingYear: number): boolean => selectedYear < reportingYear;

/**
 * Checks if the selected year is a future year (after reporting year).
 */
export const isFutureYear = (selectedYear: number, reportingYear: number): boolean => selectedYear > reportingYear;

export const formatPayoutDisplayValue = (value: number, currency?: CurrencyCode): string => formatNumericCurrency(value, { decimals: 0, currency });

export const formatPerHaValue = (totalPayout: number, totalHa: number, currency?: CurrencyCode): string => {
  if (totalHa === 0) return `${formatNumericCurrency(0, { decimals: 0, currency })}/ha`;
  const perHa = totalPayout / totalHa;
  return `${formatNumericCurrency(perHa, { decimals: 0, currency })}/ha`;
};

export const formatPerHaRange = (minPayout: number, maxPayout: number, totalHa: number, currency?: CurrencyCode): string => {
  if (totalHa === 0) return `${formatNumericCurrency(0, { decimals: 0, currency })}/ha`;
  return `${formatCurrencyRange(minPayout / totalHa, maxPayout / totalHa, { decimals: 0, currency })}/ha`;
};

export const formatCo2DisplayValue = (tonnesCo2: number): string => {
  const sign = tonnesCo2 > 0 ? "+" : "";
  const formatted = Math.round(Math.abs(tonnesCo2));
  return `${sign}${tonnesCo2 < 0 ? "-" : ""}${formatted} t CO2`;
};

export const getPayoutProgressState = (actual: number, eligible: number): { ratio: number; state: PayoutProgressState } | null => {
  if (eligible <= 0) return null;

  const ratio = Math.max(0, actual) / eligible;

  if (ratio > 0.5) return { ratio, state: "onTrack" };
  if (ratio >= 0.25) return { ratio, state: "keepRolling" };
  return { ratio, state: "needsAttention" };
};

export const buildFiveYearMonitoringWindow = (firstMonitoringYear: number): number[] => Array.from({ length: 5 }, (_, index) => firstMonitoringYear + index);

export const resolveBaselineSeries = (yearsToValues: Record<number, number>): { years: number[]; values: number[]; average: number; isExample: boolean } => {
  const sortedYears = Object.keys(yearsToValues)
    .map(Number)
    .sort((a, b) => a - b);

  if (sortedYears.length === 0) {
    const exampleValues = [80, -10, 131];
    const exampleYears = [2020, 2021, 2022];
    const average = Math.round(exampleValues.reduce((sum, value) => sum + value, 0) / exampleValues.length);
    return { years: exampleYears, values: exampleValues, average, isExample: true };
  }

  const firstThreeYears = sortedYears.slice(0, 3);
  const values = firstThreeYears.map((year) => yearsToValues[year]);
  const hasOnlyZeroValues = values.every((value) => value === 0);
  if (hasOnlyZeroValues) {
    const exampleValues = [80, -10, 131];
    const average = Math.round(exampleValues.reduce((sum, value) => sum + value, 0) / exampleValues.length);
    return { years: firstThreeYears, values: exampleValues, average, isExample: true };
  }

  const average = Math.round(values.reduce((sum, value) => sum + value, 0) / Math.max(values.length, 1));

  return { years: firstThreeYears, values, average, isExample: false };
};
